import React, { useEffect, useState, useContext } from 'react';
import * as R from 'ramda';
import Button from '@mui/material/Button';
import { noop, either, convertDateToLocalTime } from '@cd/utils';
import { postJSONPS } from '@cd/utils';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import styles from './css/PaymentSuccess.scss';
import { CenterProgress } from '../component/CenterProgress';
import PDFView from './PDFView';
import { store } from './store';

const titleSx = theme => ({
	marginTop: theme.spacing(2)
});

const notifyAboutPaymentFromClient = ({ gatewayReponse }) =>
	postJSONPS('POST', '/notify/payment/from/client', {
		body: JSON.stringify({ gatewayReponse })
	});

// The server settles the payment itself when Stripe's webhook has not landed
// yet, so the first answer normally has the details. Poll a little for the
// rare slow case, then stop: the charge already succeeded, only the receipt
// is pending.
const MAX_ATTEMPTS = 5;
const RETRY_MS = 2000;

function PaymentSuccess({
	location: { state },
	match: {
		params: { transactionID }
	},
	postError = noop,
	invoices = []
}) {
	const { dispatch } = useContext(store);
	const [data, setData] = useState(null);
	const [gaveUp, setGaveUp] = useState(false);

	useEffect(() => {
		let cancelled = false;
		let timer;
		let notified = false;

		const attempt = n =>
			postJSONPS('POST', '/pay/success/details', {
				body: JSON.stringify({ transactionID })
			}).then(
				either(
					e => {
						if (cancelled) return;
						postError(e);
						setGaveUp(true);
					},
					details => {
						if (cancelled) return;
						if (!R.isNil(details) && !R.isEmpty(details)) {
							setData(details);
							return;
						}
						// audit copy of what Stripe told the browser, once
						if (!notified && state) {
							notified = true;
							notifyAboutPaymentFromClient({ gatewayReponse: state });
						}
						if (n < MAX_ATTEMPTS) {
							timer = setTimeout(() => attempt(n + 1), RETRY_MS);
						} else {
							setGaveUp(true);
						}
					}
				)
			);

		attempt(1);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, []);

	function onViewPDF(e) {
		dispatch({
			type: 'open dialog',
			dlgCmp: (
				<PDFView
					url={`/api/receipt/view/RECEIPT_${transactionID}.pdf`}
				/>
			)
		});
	}
	return (
		<div className={styles.center}>
			<Typography
				variant="h6"
				gutterBottom
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}}>
				<CheckCircleOutlineIcon
					style={{ width: 'auto', height: '40px', color: 'green' }}
				/>
				Payment Successful!
			</Typography>

			{data && !R.isEmpty(data) ? (
				<Grid container spacing={2}>
					<Grid size={{ xs: 12, sm: 12 }}>
						<Typography
							variant="h6"
							gutterBottom
							sx={titleSx}>
							Your payment information has been sent to your email
						</Typography>
						<Typography gutterBottom>
							Amount : {data.amount}
						</Typography>
						<Typography gutterBottom>
							Date Paid :{' '}
							{convertDateToLocalTime(
								'MM/dd/yyyy HH:mm:ssaa zzz',
								data.updated
							)}
						</Typography>
						<Button variant="outlined" onClick={onViewPDF}>
							VIEW RECEIPT (PDF)
						</Button>
					</Grid>
				</Grid>
			) : gaveUp ? (
				<Typography gutterBottom sx={titleSx}>
					Your payment went through. The receipt is still being
					prepared and will be sent to your email shortly.
				</Typography>
			) : (
				<CenterProgress />
			)}
		</div>
	);
}

export default PaymentSuccess;
